// src/pages/TestTaking.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase/config';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
} from 'firebase/firestore';
import QuestionRenderer from '../components/QuestionRenderer';
import '../styles/TestTaking.css';

// Pass threshold for placement tests
const PLACEMENT_PASS_THRESHOLD = 65;

// Ordered placement levels
const PLACEMENT_LEVELS = ['level-1', 'level-2', 'level-3', 'level-4'];

// Advanced levels unlocked when a learner passes Level 4
const ADVANCED_LEVELS = ['level-5', 'level-6', 'level-7', 'level-8', 'level-9', 'level-10'];

const TestTaking = ({ isLessonTest = false }) => {
  const { testId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [attemptId, setAttemptId] = useState(null);

  useEffect(() => {
    loadTestAndQuestions();
  }, [testId]);

  const loadTestAndQuestions = async () => {
    try {
      const collectionName = isLessonTest ? 'lessonTests' : 'tests';
      const testDoc = await getDoc(doc(db, collectionName, testId));

      if (!testDoc.exists()) {
        alert('Test not found');
        navigate('/learner');
        return;
      }

      const testData = { id: testDoc.id, ...testDoc.data() };
      setTest(testData);

      let selectedQuestions;

      if (isLessonTest) {
        const allQuestions = testData.questions || [];
        const maxToShow = testData.maxQuestions || allQuestions.length;
        selectedQuestions = shuffleArray(allQuestions).slice(0, maxToShow);

        if (selectedQuestions.length === 0) {
          alert('No questions available for this test.');
          navigate('/learner');
          return;
        }
      } else {
        if (testData.selectedQuestionIds && testData.selectedQuestionIds.length > 0) {
          const questionsPromises = testData.selectedQuestionIds.map(id =>
            getDoc(doc(db, 'testQuestions', id))
          );
          const questionDocs = await Promise.all(questionsPromises);
          selectedQuestions = questionDocs
            .filter(d => d.exists())
            .map(d => ({ id: d.id, ...d.data() }));
        } else {
          let questionsQuery;

          if (testData.testTypes && Array.isArray(testData.testTypes)) {
            questionsQuery = query(
              collection(db, 'testQuestions'),
              where('levelId', '==', testData.levelId),
              where('testType', 'in', testData.testTypes)
            );
            if (testData.lessonCode || testData.oldLessonCode) {
              questionsQuery = query(
                collection(db, 'testQuestions'),
                where('levelId', '==', testData.levelId),
                where('testType', 'in', testData.testTypes),
                where('lessonCode', '==', testData.lessonCode || testData.oldLessonCode)
              );
            }
          } else {
            questionsQuery = query(
              collection(db, 'testQuestions'),
              where('levelId', '==', testData.levelId),
              where('testType', '==', testData.testType)
            );
            if (testData.lessonCode || testData.oldLessonCode) {
              questionsQuery = query(
                collection(db, 'testQuestions'),
                where('levelId', '==', testData.levelId),
                where('testType', '==', testData.testType),
                where('lessonCode', '==', testData.lessonCode || testData.oldLessonCode)
              );
            }
          }

          const questionsSnapshot = await getDocs(questionsQuery);
          const allQuestions = questionsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          const numberOfQuestions = testData.settings?.numberOfQuestions || 15;
          selectedQuestions = shuffleArray(allQuestions).slice(0, numberOfQuestions);

          if (selectedQuestions.length < numberOfQuestions) {
            alert(`Not enough questions available. Found ${selectedQuestions.length}, need ${numberOfQuestions}`);
          }
        }
      }

      setQuestions(selectedQuestions);

      // Create initial attempt record
      const attemptData = {
        testId: testId,
        testType: isLessonTest ? 'lessonTest' : 'placementTest',
        userId: currentUser.uid,
        selectedQuestions: selectedQuestions.map(q => q.id),
        startedAt: new Date().toISOString(),
        status: 'in-progress',
      };

      const attemptRef = await addDoc(collection(db, 'testAttempts'), attemptData);
      setAttemptId(attemptRef.id);

    } catch (error) {
      console.error('Error loading test:', error);
      alert('Failed to load test. Please try again.');
      navigate('/learner');
    } finally {
      setLoading(false);
    }
  };

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const handleAnswerChange = (questionId, answer) => {
    setUserAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  /**
   * Find the catalogId for a given levelId.
   * Looks up the 'catalogs' collection for a doc where levelId matches.
   */
  const getCatalogIdForLevel = async (levelId) => {
    const snap = await getDocs(
      query(collection(db, 'catalogs'), where('levelId', '==', levelId))
    );
    if (snap.empty) return null;
    return snap.docs[0].id;
  };

  /**
   * Enroll the current user into a catalog, if not already enrolled.
   */
  const enrollUserInCatalog = async (catalogId) => {
    if (!catalogId) return;

    // Check if already enrolled
    const existing = await getDocs(
      query(
        collection(db, 'userCatalogs'),
        where('userId', '==', currentUser.uid),
        where('catalogId', '==', catalogId)
      )
    );

    if (!existing.empty) return; // already enrolled

    await addDoc(collection(db, 'userCatalogs'), {
      userId: currentUser.uid,
      catalogId: catalogId,
      enrolledAt: new Date().toISOString(),
      enrolledBy: 'placement-test',
    });
  };

  /**
   * Handle post-result enrollment side-effects for placement tests.
   * - On fail: enroll in the failed level's catalog so they study it.
   * - On pass level-4: enroll in all advanced level catalogs.
   */
  const handlePlacementEnrollment = async (levelId, passed) => {
    if (!passed) {
      // Enroll in this level so they can study and retry
      const catalogId = await getCatalogIdForLevel(levelId);
      await enrollUserInCatalog(catalogId);
    } else if (levelId === 'level-4') {
      // Passed the final placement level — enroll in all advanced levels
      await Promise.all(
        ADVANCED_LEVELS.map(async (advLevelId) => {
          const catalogId = await getCatalogIdForLevel(advLevelId);
          await enrollUserInCatalog(catalogId);
        })
      );
    }
    // Passing levels 1-3: no enrollment needed — they move to the next level test
  };

  const handleSubmit = async () => {
    if (!window.confirm('Are you sure you want to submit your test? You cannot change your answers after submission.')) {
      return;
    }

    setSubmitting(true);

    try {
      const results = questions.map(question => {
        const userAnswer = userAnswers[question.id];
        const isCorrect = checkAnswer(question, userAnswer);
        return { questionId: question.id, userAnswer, isCorrect };
      });

      const correctCount = results.filter(r => r.isCorrect).length;
      const score = Math.round((correctCount / questions.length) * 100);

      // Use PLACEMENT_PASS_THRESHOLD for placement tests; honour test setting for lesson tests
      const threshold = isLessonTest
        ? (test.settings?.passThreshold ?? 65)
        : PLACEMENT_PASS_THRESHOLD;

      const passed = score >= threshold;

      // Persist completed attempt
      const completedAttemptRef = await addDoc(collection(db, 'testAttempts'), {
        testId: testId,
        levelId: test.levelId,
        testType: isLessonTest ? 'lessonTest' : 'placementTest',
        userId: currentUser.uid,
        selectedQuestions: questions.map(q => q.id),
        userAnswers: userAnswers,
        results: results,
        score: score,
        passed: passed,
        passThreshold: threshold,
        startedAt: new Date().toISOString(),
        completedAt: new Date().toISOString(),
        status: 'completed',
      });

      // Side-effect: enrollment based on outcome (placement tests only)
      if (!isLessonTest && test.mode === 'placement') {
        await handlePlacementEnrollment(test.levelId, passed);
      }

      navigate(`/results/${completedAttemptRef.id}`);

    } catch (error) {
      console.error('Error submitting test:', error);
      alert('Failed to submit test. Please try again.');
      setSubmitting(false);
    }
  };

  const checkAnswer = (question, userAnswer) => {
    const content = question.content;

    switch (question.questionType) {
      case 'mcq': {
        const correctOption = content.options?.find(o => o.isCorrect);
        return correctOption?.id === userAnswer;
      }

      case 'multiple-response': {
        const correctOptions = content.options?.filter(o => o.isCorrect).map(o => o.id) || [];
        if (!userAnswer || !Array.isArray(userAnswer)) return false;
        return (
          correctOptions.length === userAnswer.length &&
          correctOptions.every(id => userAnswer.includes(id))
        );
      }

      case 'type-in': {
        if (!userAnswer || !Array.isArray(userAnswer)) return false;
        return content.answers?.every((answer, index) => {
          const correctAnswers = [
            answer.text,
            ...(answer.alternatives ? answer.alternatives.split(',').map(a => a.trim()) : []),
          ];
          return correctAnswers.some(
            correct => correct.toLowerCase() === (userAnswer[index] || '').toLowerCase()
          );
        });
      }

      case 'order': {
        if (!userAnswer || !Array.isArray(userAnswer)) return false;
        return content.items?.every((item, index) => userAnswer[index] === item.id);
      }

      case 'matching': {
        if (!userAnswer || typeof userAnswer !== 'object') return false;
        return content.pairs?.every(pair => userAnswer[pair.left] === pair.right);
      }

      default:
        return false;
    }
  };

  const getAnsweredCount = () => Object.keys(userAnswers).length;

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
        <p>Loading test...</p>
      </div>
    );
  }

  if (!test || questions.length === 0) {
    return (
      <div className="error-screen">
        <h2>Test not available</h2>
        <button onClick={() => navigate('/learner')}>Back to Dashboard</button>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <div className="test-taking">
      <div className="test-header">
        <div className="test-info">
          <h1>{isLessonTest ? test.lessonTitle : test.title}</h1>
          <p className="test-meta">
            {isLessonTest ? '📖 Knowledge Check' : '📊 Placement Test'} •{' '}
            Pass threshold: {isLessonTest ? (test.settings?.passThreshold ?? 65) : PLACEMENT_PASS_THRESHOLD}%
          </p>
        </div>
        <button
          onClick={() => {
            if (window.confirm('Are you sure you want to exit? Your progress will be lost.')) {
              navigate('/learner');
            }
          }}
          className="exit-btn"
        >
          Exit
        </button>
      </div>

      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
      </div>

      <div className="test-container">
        <div className="question-header">
          <span className="question-number">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
          <span className="answered-count">
            Answered: {getAnsweredCount()}/{questions.length}
          </span>
        </div>

        <div className="question-content">
          <QuestionRenderer
            question={currentQuestion}
            userAnswer={userAnswers[currentQuestion.id]}
            onAnswerChange={(answer) => handleAnswerChange(currentQuestion.id, answer)}
            showFeedback={false}
          />
        </div>

        <div className="navigation-buttons">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="nav-btn prev-btn"
          >
            ← Previous
          </button>

          {currentQuestionIndex === questions.length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="submit-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Test'}
            </button>
          ) : (
            <button onClick={handleNext} className="nav-btn next-btn">
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestTaking;